import { Frame } from "game-common/ember_sprite/raster"

import { PixelBuffer, bayer, rgb } from "../../intro/pixel_raster"
import { ArtRect } from "./creation_layout"

// The figure on the deck, as pixels: one standing frame from the body generator put down on the feet
// line, the dither that carries it from one look (or facing) to the next, its short shadow on the
// planks, and the warm rim the lantern puts on the side nearest it.
//
// Pure: frames and buffers in, buffers out.

const SHADOW = rgb(0x0b0b13, 110)

/** A frame's lowest drawn row: where the feet stand. */
export const groundRowOf = (frame: Frame): number => {
    for (let y = frame.height - 1; y >= 0; y--) {
        for (let x = 0; x < frame.width; x++) {
            if (frame.pixels[(y * frame.width + x) * 4 + 3] > 0) return y
        }
    }
    return frame.height - 1
}

/**
 * The frame over `box`, its middle on `figX` and its ground row (`ground`, measured once per body so a
 * boot heel can't hop it) on `feetY`. `hop` lifts it that many pixels.
 */
export const stampFigure = (box: ArtRect, frame: Frame, ground: number, figX: number, feetY: number, hop: number = 0): PixelBuffer => {
    const out = new PixelBuffer(box.w, box.h, box.x, box.y)
    const source = new Uint32Array(frame.pixels.buffer, frame.pixels.byteOffset, frame.width * frame.height)
    const x0 = figX - Math.floor(frame.width / 2)
    for (let j = 0; j < frame.height; j++) {
        for (let i = 0; i < frame.width; i++) {
            const c = source[j * frame.width + i]
            if (c >>> 24) out.set(x0 + i, feetY - (ground - j) - hop, c)
        }
    }
    return out
}

/**
 * The intro's ordered dither from `from` (or nothing) to `to`: at `progress` 0 every pixel is the old
 * figure's, at 1 the new one's, and in between each pixel is one or the other -- never a blend.
 */
export const ditherFigures = (from: PixelBuffer | undefined, to: PixelBuffer, progress: number): PixelBuffer => {
    if (progress >= 1) return to
    const out = new PixelBuffer(to.w, to.h, to.x0, to.y0)
    for (let j = 0; j < to.h; j++) {
        for (let i = 0; i < to.w; i++) {
            const x = to.x0 + i
            const y = to.y0 + j
            const k = j * to.w + i
            out.data[k] = bayer(x, y) < progress ? to.data[k] : from ? from.data[k] : 0
        }
    }
    return out
}

/**
 * The layer the page shows: the figure's short shadow on the planks (the lantern hangs above and to the
 * left, so it falls away to the right), then the figure, its edge toward the lantern warmed.
 */
export const lightFigure = (figure: PixelBuffer, isPlank: (x: number, y: number) => boolean, figX: number, feetY: number): PixelBuffer => {
    const out = new PixelBuffer(figure.w, figure.h, figure.x0, figure.y0)
    const drawn = (x: number, y: number) => (figure.get(x, y) >>> 24) > 0
    for (let j = 0; j < figure.h; j++) {
        const y = figure.y0 + j
        if (y > feetY) continue
        for (let i = 0; i < figure.w; i++) {
            const x = figure.x0 + i
            if (!drawn(x, y)) continue
            const up = feetY - y
            const sx = Math.round(x + up * 0.75 + (x - figX) * 0.3)
            const sy = Math.round(feetY + 1 + up * 0.1)
            for (const xx of [sx, sx + 1]) {
                if (isPlank(xx, sy) && !drawn(xx, sy)) out.set(xx, sy, SHADOW)
            }
        }
    }
    for (let j = 0; j < figure.h; j++) {
        for (let i = 0; i < figure.w; i++) {
            const c = figure.data[j * figure.w + i]
            if (!(c >>> 24)) continue
            const x = figure.x0 + i
            const y = figure.y0 + j
            out.set(x, y, drawn(x - 1, y) ? c : warm(c, 0.1))
        }
    }
    return out
}

/** The lantern's light added to a colour: warm, never a grey wash. */
export const warm = (c: number, amount: number): number => {
    const r = Math.min(255, (c & 0xff) + 255 * amount)
    const g = Math.min(255, ((c >>> 8) & 0xff) + 184 * amount)
    const b = Math.min(255, ((c >>> 16) & 0xff) + 96 * amount)
    return ((c & 0xff000000) | (Math.round(b) << 16) | (Math.round(g) << 8) | Math.round(r)) >>> 0
}
